import {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../redux/store";
import SearchInput from "../../../components/SearchInput";
import { Button } from "../../../components/button/Button";
import { ModalContext } from "../../../components/Modal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSquareCaretDown } from "@fortawesome/free-solid-svg-icons";
import { fetchMembersMembers } from "../../../redux/queryMetaSlice";
import Checkbox from "../../../components/checkbox/Checkbox";
import {
  ColumnMenuModule,
  ColumnsToolPanelModule,
  ContextMenuModule,
  CellStyleModule,
  FiltersToolPanelModule,
  SetFilterModule,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
  ClientSideRowModelModule,
  DateFilterModule,
  ModuleRegistry,
  NumberFilterModule,
  TextFilterModule,
  ValidationModule,
  RowSelectionModule,
  RowSelectionOptions,
} from "ag-grid-community";
import useAgGridTheme from "../../../gridUtils/agGridTheme";
import {
  DEFAULT_COL_DEF,
  DEFAULT_GRID_OPTIONS,
} from "../../../gridUtils/gridUtils";
import {
  defaultFilterSelections,
  FilterBoxProps,
  FilterOption,
  TimeFilterOption,
  TimeCategoryOption,
} from "../../../interfaces/queryBuilderInterfaces";
import useTimeRowSelection from "../../../gridUtils/useRowSelection";
import { TimePeriodDropdown } from "../../../components/dropdown/TimePeriodDropdown";

ModuleRegistry.registerModules([
  RowSelectionModule,
  ClientSideRowModelModule,
  ColumnsToolPanelModule,
  FiltersToolPanelModule,
  ColumnMenuModule,
  ContextMenuModule,
  CellStyleModule,
  SetFilterModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  ValidationModule,
]);

const FilterBox = ({
  treeData,
  filterSelections,
  applyFilter,
  advancedFilterGridRef,
  advancedFilterGridColumn,
  handleCheckAll,
  handleUncheckAll,
  onRowSelected,
  onCellClicked,
  selectRowsByArray,
  setOptionsLength,
  currentSelections,
  activePanelTab,
  isExclude,
  setExclude,
  setIsNameSetListMode,
}: FilterBoxProps) => {
  const [filterOptions, setFilterOptions] = useState<TimeFilterOption[]>([]);
  const [options, setOptions] = useState<TimeFilterOption[]>([]);
  const [isShowSelected, setShowSelected] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [style, setStyle] = useState<CSSProperties>({ height: "220px" });
  const dispatch: AppDispatch = useDispatch();
  const modalContext = useContext(ModalContext);
  const close = modalContext ? modalContext.close : () => {};
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const [applyTo, setApplyTo] = useState<string | null>(null);
  const menuOptions = ["Rows", "Columns", "Filters"];
  const defaultOption = treeData.name === "Time Period" ? "Columns" : "Rows";
  const [showError, setShowError] = useState(false);

  const [searchText, setSearchText] = useState("");

  const rowSelection = useMemo<
    RowSelectionOptions | "single" | "multiple"
  >(() => {
    return { mode: "multiRow" };
  }, []);

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  const excludeHandler = () => {
    if (setExclude) {
      setExclude(!isExclude);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  const showSelectedHandler = () => {
    handleShowSelectedInternal(
      !isShowSelected,
      undefined,
      undefined,
      searchText,
    );
    setShowSelected(!isShowSelected);
  };

  const search = (value: string) => {
    //searchInternal(value);
    handleShowSelectedInternal(isShowSelected, undefined, undefined, value);
    setSearchText(value);
  };
  /*const applyFilterHandler = (applyOption: string) => {
        if(showError) {
            return;
        }
        applyFilter({ selectedFilters: currentSelections.map((i:any) => i.name), applyTo: applyOption, exclude: isExclude });
        close();
        defaultFilterSelections.selectedFilters = [];
    }
    const removeFilterHandler = () => {
        defaultFilterSelections.selectedFilters = [];
        applyFilter(defaultFilterSelections);
        close();
    }*/

  const fetchData = async (uniquename: string) => {
    const data = await dispatch(fetchMembersMembers(uniquename));

    setOptions(data.payload);
    setFilterOptions(data.payload);
    setOptionsLength(data.payload ? data.payload.length : 0);
    setLoading(false);
  };
  const [selectedCategory, setSelectedCategory] =
    useState<TimeCategoryOption>();
  const [selectedSubCategory, setSelectedSubCategory] =
    useState<TimeCategoryOption>();

  const handleCategoryChange = (selectedValue: string | TimeCategoryOption) => {
    if (typeof selectedValue === "string") return; // Ignore string values
    setSelectedCategory(selectedValue);
    setSelectedSubCategory(undefined); // Reset subcategory when category changes
    if (selectedValue === null) {
      setFilterOptions(options);
    } else {
      // Get only selected rows that belong to the current category
      const selectedRowsInCategory = options.filter(
        (item) =>
          selectedValue.isMember(item) &&
          currentSelections?.some((selectedRow: any) => selectedRow.name === item.name),
      );
      
      // Filter options by current category
      const filteredByCategory = options.filter(
        (item) =>
          selectedValue.isMember(item) &&
          (searchText.length === 0 ||
            item.caption.toLowerCase().includes(searchText.toLowerCase())),
      );
      
      // Combine filtered items with selected items from this category
      setFilterOptions([
        ...filteredByCategory,
        ...selectedRowsInCategory.filter(
          (selectedRow) =>
            !filteredByCategory.some((filteredRow) => filteredRow.name === selectedRow.name),
        ),
      ]);
    }
    handleShowSelectedInternal(
      isShowSelected,
      selectedValue,
      undefined,
      searchText,
    );
  };

  const handleSubCategoryChange = (
    selectedSubValue: string | TimeCategoryOption,
  ) => {
    if (typeof selectedSubValue === "string") return; // Ignore string values
    setSelectedSubCategory(selectedSubValue);
    if (selectedSubValue === null) {
      // If subcategory is cleared, show category items
      if (selectedCategory) {
        const selectedRowsInCategory = options.filter(
          (item) =>
            selectedCategory.isMember(item) &&
            currentSelections?.some((selectedRow: any) => selectedRow.name === item.name),
        );
        const filteredByCategory = options.filter(
          (item) =>
            selectedCategory.isMember(item) &&
            (searchText.length === 0 ||
              item.caption.toLowerCase().includes(searchText.toLowerCase())),
        );
        setFilterOptions([
          ...filteredByCategory,
          ...selectedRowsInCategory.filter(
            (selectedRow) =>
              !filteredByCategory.some((filteredRow) => filteredRow.name === selectedRow.name),
          ),
        ]);
      } else {
        setFilterOptions(options);
      }
    } else {
      // Get only selected rows that belong to the current subcategory
      const selectedRowsInSubCategory = options.filter(
        (item) =>
          selectedSubValue.isMember(item) &&
          currentSelections?.some((selectedRow: any) => selectedRow.name === item.name),
      );
      
      // Filter options by current subcategory
      const filteredBySubCategory = options.filter(
        (item) =>
          selectedSubValue.isMember(item) &&
          (searchText.length === 0 ||
            item.caption.toLowerCase().includes(searchText.toLowerCase())),
      );
      
      // Combine filtered items with selected items from this subcategory
      setFilterOptions([
        ...filteredBySubCategory,
        ...selectedRowsInSubCategory.filter(
          (selectedRow) =>
            !filteredBySubCategory.some((filteredRow) => filteredRow.name === selectedRow.name),
        ),
      ]);
    }
    handleShowSelectedInternal(
      isShowSelected,
      undefined,
      selectedSubValue,
      searchText,
    );
  };
  useEffect(() => {
    if (filterSelections && options && options.length > 0) {
      let selectedObjects: FilterOption[] = [];
      if (currentSelections) {
        selectedObjects = options.filter((item) =>
          currentSelections.map((i: any) => i.name).includes(item.name),
        );
      }
      selectRowsByArray?.(
        selectedObjects.map((item) => ({
          ...item,
          altCaption: item.caption, // Provide a default or derived value for altCaption
          properties: {}, // Provide a default or derived value for properties
        })),
      );
      //setExclude(filterSelections.exclude);
      // setApplyTo(filterSelections.applyTo);
      if (
        treeData.name === "Time Period" &&
        (!currentSelections || currentSelections.length === 0)
      ) {
        setShowError(true);
      } else {
        setShowError(false);
      }
    }
  }, [options, filterOptions, currentSelections]);

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  }, [showMenu]);

  const handleShowSelectedInternal = (
    flag: boolean,
    selectedOption1: TimeCategoryOption | undefined,
    selectedOption2: TimeCategoryOption | undefined,
    searchVal: string,
  ) => {
    let categoryToCheck: TimeCategoryOption | undefined = selectedOption1;
    let subCategoryToCheck: TimeCategoryOption | undefined = selectedOption2;

    if (categoryToCheck === undefined && subCategoryToCheck === undefined) {
      categoryToCheck = selectedCategory;
      subCategoryToCheck = selectedSubCategory;
    }

    let filteredOptions = options.filter((item) => {
      if (subCategoryToCheck) {
        return subCategoryToCheck.isMember(item);
      } else if (categoryToCheck) {
        return categoryToCheck.isMember(item);
      } else {
        return true;
      }
    });
    if (flag) {
      filteredOptions = filteredOptions.filter(
        (item) =>
          currentSelections &&
          currentSelections.some(
            (selectedRow: any) => selectedRow.name === item.name,
          ),
      );
    }
    if (searchVal !== "") {
      const searchItems = searchVal
        .split(",")
        .map((s) => s.trim().toLowerCase())
        .filter((s) => s.length > 0);
      if (searchItems.length > 0) {
        const prefixMatches = filteredOptions.filter((item) =>
          searchItems.some((searchItem) =>
            item.caption.toLowerCase().startsWith(searchItem),
          ),
        );
        const containsMatches = filteredOptions.filter(
          (item) =>
            !searchItems.some((searchItem) =>
              item.caption.toLowerCase().startsWith(searchItem),
            ) &&
            searchItems.some((searchItem) =>
              item.caption.toLowerCase().includes(searchItem),
            ),
        );
        filteredOptions = [...prefixMatches, ...containsMatches];
      }
    }
    setFilterOptions(filteredOptions);
  };

  //setup filter as Calendar Years when loading
  useEffect(() => {
    if (treeData.name === "Time Period" && options.length > 0) {
      handleCategoryChange(timePeriodCategories[0]);
    }
  }, [options]);

  useEffect(() => {
    fetchData(
      treeData.uniquename ? treeData.uniquename : treeData.levels[0].uniquename,
    );
  }, [dispatch]);

  const timePeriodCategories = [
    {
      name: "Calendar Years",
      ord: 1,
      isMember: function (member: any) {
        return member.properties.Type == "1";
      },
    },
    {
      name: "Rolling Time Period",
      ord: 2,
      isMember: function (member: any) {
        return member.properties.Type == "2";
      },
      subCategories: [
        {
          name: "Rolling Years",
          ord: 1,
          isMember: function (member: any) {
            return member.properties.Type == "2";
          },
        },
        {
          name: "January",
          ord: 2,
          isMember: function (member: any) {
            return member.properties.Type == "7.01";
          },
        },
        {
          name: "February",
          ord: 3,
          isMember: function (member: any) {
            return member.properties.Type == "7.02";
          },
        },
        {
          name: "March",
          ord: 4,
          isMember: function (member: any) {
            return member.properties.Type == "7.03";
          },
        },
        {
          name: "April",
          ord: 5,
          isMember: function (member: any) {
            return member.properties.Type == "7.04";
          },
        },
        {
          name: "May",
          ord: 6,
          isMember: function (member: any) {
            return member.properties.Type == "7.05";
          },
        },
        {
          name: "June",
          ord: 7,
          isMember: function (member: any) {
            return member.properties.Type == "7.06";
          },
        },
        {
          name: "July",
          ord: 8,
          isMember: function (member: any) {
            return member.properties.Type == "7.07";
          },
        },
        {
          name: "August",
          ord: 9,
          isMember: function (member: any) {
            return member.properties.Type == "7.08";
          },
        },
        {
          name: "September",
          ord: 10,
          isMember: function (member: any) {
            return member.properties.Type == "7.09";
          },
        },
        {
          name: "October",
          ord: 11,
          isMember: function (member: any) {
            return member.properties.Type == "7.1";
          },
        },
        {
          name: "November",
          ord: 12,
          isMember: function (member: any) {
            return member.properties.Type == "7.11";
          },
        },
        {
          name: "December",
          ord: 13,
          isMember: function (member: any) {
            return member.properties.Type == "7.12";
          },
        },
      ],
    },
    {
      name: "Quarters",
      ord: 4,
      isMember: function (member: any) {
        return member.properties.Type == "4";
      },
    },
    {
      name: "Months",
      ord: 5,
      isMember: function (member: any) {
        return member.properties.Type == "5";
      },
    },
  ];

  const categoryOptions = timePeriodCategories.map((category) => ({
    value: category,
    label: category.name,
  }));

  const selectedCategoryObj =
    typeof selectedCategory === "object" && "ord" in selectedCategory
      ? timePeriodCategories.find(
          (category) => category.ord === selectedCategory.ord,
        )
      : undefined;
  const subCategories = selectedCategoryObj?.subCategories || [];
  const subCategoryOptions = subCategories.map((sub) => ({
    value: sub,
    label: sub.name,
  }));

  return (
    <div className="filter-dialog-container dialog-box-min filter-box">
      <div className="dialog-body nopadding">
        <div>
          <div className="search-filter">
            <SearchInput
              callback={search}
              debounce={300}
              placeholder="Search"
            />
          </div>
          {treeData.caption === "Time Period" && (
            <div className="time-dropdown-section">
              <div className="time-dropdown">
                <TimePeriodDropdown
                  id={"time-periods-dropdown"}
                  selectedValue={selectedCategory || ""}
                  onChange={handleCategoryChange}
                  options={categoryOptions}
                />
                {subCategories.length > 0 && (
                  <TimePeriodDropdown
                    id={"sub-categories-dropdown"}
                    selectedValue={selectedSubCategory || ""}
                    onChange={handleSubCategoryChange}
                    options={subCategoryOptions}
                  />
                )}
              </div>
              <div className="time-period-label">
                <label className="time-label-text">
                  {treeData &&
                    "levels" in treeData &&
                    Array.isArray(treeData.levels) &&
                    treeData.levels.length > 0 &&
                    treeData.levels[0].properties &&
                    treeData.levels[0].properties["filter.custom.label"]}
                </label>
              </div>
            </div>
          )}

          <div className="filter-options-section">
            <div className="filter-checkbox-section">
              <label className="radio-inline" htmlFor="show-selected-radio">
                <Checkbox
                  id="show-selected-radio"
                  name="show-selected"
                  checked={isShowSelected}
                  onChange={showSelectedHandler}
                  value={""}
                  styleClass={""}
                  color={""}
                />
                <span className="ellipsis">Show Selected</span>
              </label>
              {treeData.name !== "Time Period" &&
                activePanelTab == "displayFields" && (
                  <label className="radio-inline" htmlFor="excluded-radio">
                    <Checkbox
                      id="excluded-radio"
                      name="exclude"
                      checked={!!isExclude}
                      onChange={excludeHandler}
                      value={""}
                      styleClass={""}
                      color={""}
                    />
                    <span className="ellipsis">Exclude</span>
                  </label>
                )}
            </div>

            <div className="filter-button-section">
              <Button
                disabled={filterOptions && filterOptions.length > 999}
                color="primary"
                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={handleCheckAll}
              >
                Select All
              </Button>
              <Button
                color="primary"
                className="btn md"
                variant="plain"
                id="btn-apply"
                onClick={handleUncheckAll}
              >
                Deselect All
              </Button>
            </div>
          </div>

          <div className="ag-theme-alpine grid-wrapper grid-multi-select">
            <div style={style}>
              <AgGridReact<TimeFilterOption>
                theme={useAgGridTheme("compact")}
                ref={advancedFilterGridRef}
                rowData={filterOptions}
                columnDefs={advancedFilterGridColumn}
                defaultColDef={DEFAULT_COL_DEF}
                rowSelection={rowSelection}
                gridOptions={DEFAULT_GRID_OPTIONS}
                onRowSelected={onRowSelected}
                onCellClicked={onCellClicked}
                suppressContextMenu={true}
                headerHeight={0}
                onGridReady={(params) => {
                  if (isLoading) {
                    params.api.showLoadingOverlay();
                  } else {
                    params.api.hideOverlay();
                  }
                }}
              />
            </div>
          </div>
        </div>
      </div>
      {showError && (
        <div style={{ fontSize: "12px", color: "red" }}>
          Must select at least one filter value for this field
        </div>
      )}
      {/*currentSelections && currentSelections.length > 0 && 
                <div className="selected-filters">  
                    <span>Selection: {isExclude ? `Excluding ${currentSelections.length} value(s)` : `Including ${currentSelections.length} value(s)`}</span>
                </div>
            */}
      {/* <div className="dialog-footer">
                
                <div className="apply-cancel">
                    {treeData.name !== 'Time Period' && <Button color="primary" className="btn md" variant="solid" id="btn-apply" onClick={removeFilterHandler}>Remove</Button>}
                    <Button color="secondary" className="btn md" variant="solid" id="btn-close" onClick={close}>Cancel</Button>
                </div>
                <div className="add-filters-button" ref={menuRef}>
                    <span style={{ fontWeight: 'bold' }}>Apply as</span>
                    <Button color="secondary" className="btn md selected-btn" variant="solid" id="btn-add-row" onClick={() => applyFilterHandler(applyTo ? applyTo : defaultOption)}>{`${applyTo ? applyTo : defaultOption}`}</Button>
                    <Button color="secondary" className="btn md dropdown-icon" variant="solid" id="btn-add-column" onClick={toggleMenu}><FontAwesomeIcon icon={faSquareCaretDown} /></Button>
                    {showMenu && (<div className="menu-options">
                        {[...menuOptions.filter(item => (applyTo ? item !== applyTo : item !== defaultOption))].map((option, index) => (<Button color="secondary" key={index} className="btn md" variant="plain" onClick={() => applyFilterHandler(option)}>{`${option}`}</Button>))}
                    </div>)}

                </div>
            </div> */}
    </div>
  );
};

export default FilterBox;
